import QRCodeStyling from "qr-code-styling";
import CreatePayload from "../../api/qr-code/CreatePayload";
import {saveAs} from "file-saver";
import DownloadPayload, {toFullPayload} from "./DownloadPayload";
import QRCodeKind from "../../api/qr-code/QRCodeKind";

/**
 * Static class to help with QR codes.
 * @author Kacper Faber
 * @since 0.1.8
 */
class QRCodeHelper {
    /**
     * Get kind as human-readable text.
     * @throws Error if kind is unsupported
     * @author Kacper Faber
     * @since 0.2.1
     */
    static getKindText(kind: QRCodeKind | undefined): string {
        if (!kind) {
            return '<Brak formatu>';
        }

        if (kind == 'vcard') {
            return 'Wizytówka';
        }

        else if (kind == 'website') {
            return 'Strona internetowa';
        }

        else if (kind == 'image') {
            return 'Pojedyncze zdjęcie';
        }

        else if (kind == 'pdf') {
            return 'Dokument PDF';
        }

        throw new Error(`Kind ${kind} is not supported by QRCodeHelper.getKindText()`);
    };

    /**
     * Get random 12-letters key to be set as QRCode key.
     * @author Kacper Faber
     * @since 0.1.8
     */
    static getRandomKey(): string {
        // noinspection SpellCheckingInspection
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        const charactersLength = characters.length;
        for (let i = 0; i < 12; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    }

    /**
     * Generate simple QR code image in file.
     * It's using PNG format by default.
     * @author Kacper Faber
     * @since 0.1.8
     */
    static async generateSimpleFile(payload: CreatePayload): Promise<File | null> {
        const url = process.env.REACT_APP_API_URL ?? '';
        // TODO: URL, from process.env.REACT_APP_API_URL but anyway
        const generator = new QRCodeStyling({data: `${url}/a/${payload.key}`});
        const data = await generator.getRawData('png');
        return QRCodeHelper.toFile(data, payload.key + '.png', 'image/png');
    }

    /**
     * Generate file from Blob or Buffer.
     * Returns null, if given data were null.
     * @param data
     * @param fileName
     * @param mime
     */
    static toFile(data: Buffer | Blob | null, fileName: string, mime: string): File | null {
        if (data == null) {
            return null;
        }
        let blob: Blob;
        if (data instanceof ArrayBuffer || data instanceof Uint8Array) {
            blob = new Blob([data], {type: mime});
        }
        else {
            blob = data;
        }
        return new File([blob as BlobPart], fileName, {type: blob.type});
    }

    /**
     * Download the file.
     * @since 0.1.9
     * @author Kacper Faber
     */
    static async download(p: DownloadPayload): Promise<void> {
        const response = await fetch(p.imageUrl);
        if (!response.ok) {
            throw new Error('HTTP error, received status code is not OK.');
        }
        const fp = toFullPayload(p);
        const fileName = `${fp.fileName}${fp.ext}`;
        saveAs(await response.blob(), fileName);
    }
}

export default QRCodeHelper;

// TODO: QRCodeHelper.tsx
//  Introduce URLS to generate URLs [Im using process.env etc]
//  See this class and improve other things.... :C